/**
 * teams-notify.ts
 *
 * Microsoft Teams Webhook-Integration für Handoff-Benachrichtigungen.
 * Sendet eine Adaptive Card an einen Teams-Kanal wenn ein Nutzer
 * an einen menschlichen Mitarbeiter übergeben wird.
 *
 * Entscheidung: DEC-009 (Meeting 16.03.2026)
 */

import type { ConversationContext } from './handoff';

// ============================================================
// Types
// ============================================================

interface TeamsAdaptiveCard {
  type: string;
  attachments: Array<{
    contentType: string;
    content: Record<string, unknown>;
  }>;
}

// ============================================================
// Teams Webhook
// ============================================================

/**
 * Erstellt eine Adaptive Card für Teams mit dem Gesprächskontext.
 */
function buildAdaptiveCard(context: ConversationContext): TeamsAdaptiveCard {
  const durationMinutes = Math.ceil(context.duration / 60);

  return {
    type: 'message',
    attachments: [
      {
        contentType: 'application/vnd.microsoft.card.adaptive',
        content: {
          $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
          type: 'AdaptiveCard',
          version: '1.4',
          body: [
            {
              type: 'TextBlock',
              text: 'Neue Übergabe von KI-Berater',
              weight: 'Bolder',
              size: 'Large',
              color: 'Accent',
            },
            {
              type: 'FactSet',
              facts: [
                { title: 'Zeitpunkt', value: new Date().toLocaleString('de-DE') },
                { title: 'Thema', value: context.topic || 'Nicht angegeben' },
                { title: 'Dauer', value: `${durationMinutes} Minuten` },
                { title: 'Kunde', value: context.userName ?? 'Unbekannt' },
                { title: 'E-Mail', value: context.userEmail ?? 'Nicht angegeben' },
                { title: 'Firma', value: context.userCompany || 'Nicht angegeben' },
              ],
            },
            {
              type: 'TextBlock',
              text: 'Erwähnte Produkte',
              weight: 'Bolder',
              spacing: 'Medium',
            },
            {
              type: 'TextBlock',
              text: context.products.length > 0
                ? context.products.join(', ')
                : 'Keine Produkte erwähnt',
              wrap: true,
            },
            {
              type: 'TextBlock',
              text: 'Zusammenfassung',
              weight: 'Bolder',
              spacing: 'Medium',
            },
            {
              type: 'TextBlock',
              text: context.summary || 'Keine Zusammenfassung verfügbar',
              wrap: true,
            },
          ],
        },
      },
    ],
  };
}

/**
 * Sendet eine Handoff-Benachrichtigung an einen Teams-Kanal via Webhook.
 *
 * @param webhookUrl - Teams Incoming Webhook URL
 * @param context - Gesprächskontext aus der Avatar-Session
 */
export async function sendTeamsNotification(
  webhookUrl: string,
  context: ConversationContext,
): Promise<boolean> {
  try {
    const card = buildAdaptiveCard(context);

    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(card),
    });

    if (!response.ok) {
      console.error(
        `Teams-Benachrichtigung fehlgeschlagen: ${response.status} ${response.statusText}`,
      );
      return false;
    }

    return true;
  } catch (error) {
    console.error('Teams-Webhook Fehler:', error);
    return false;
  }
}
